import localStorage from "@/db/aisea";
const localDb = localStorage.getInstance();
import { MAP_DEFAULT_POSITION } from "@/config/const";
import Vue from "vue";
import { VesselMixin, PlotMixin, LayerMixin } from "@/mixins";
import authService from "@/services/authService";

const MONITORING_VESSEL = { ID: 1 };
const SETTINGS = {
  DEFAULT_POSITION_ID: 1,
  MAP_STYLE_ID: 2,
  OWN_VESSEL_DISPLAY_DURATION_TIME_ID: 3,
  LEFT_MENU_REDUCE_STATE_ID: 4,
};
const FILTERS = {
  VESSEL_ID: 1,
  PLOT_ID: 2,
  NAVIGATION_ID: 3,
  RADAR_GRID_ID: 4,
};
const DEFAULT_REDUCE_STATE = false;

class LocalDbService {
  async getMonitoringVessels() {
    let res = await localDb.monitoring_vessels.get(MONITORING_VESSEL.ID);
    if (res === undefined) {
      await localDb.monitoring_vessels.put({
        id: MONITORING_VESSEL.ID,
        vessel_ids: [],
        updated_at: new Date(),
      });
      return [];
    }
    return res.vessel_ids;
  }

  async addMonitoringVessel(vesselId) {
    let vesselIds = [];
    let res = await localDb.monitoring_vessels.get(MONITORING_VESSEL.ID);
    if (res != null && res.vessel_ids !== undefined) {
      vesselIds = res.vessel_ids;
      vesselIds.push(vesselId);
    } else {
      vesselIds.push(vesselId);
    }
    await localDb.monitoring_vessels.put({
      id: MONITORING_VESSEL.ID,
      vessel_ids: vesselIds,
      updated_at: new Date(),
    });
    return vesselIds;
  }

  async removeMonitoringVessel(vesselId) {
    let res = await localDb.monitoring_vessels.get(MONITORING_VESSEL.ID);
    let vesselIds = res.vessel_ids.filter(id => id !== vesselId);
    await localDb.monitoring_vessels.put({
      id: MONITORING_VESSEL.ID,
      vessel_ids: vesselIds,
      updated_at: new Date(),
    });
    return vesselIds;
  }

  async saveSelectedVessels(vesselIds) {
    await localDb.monitoring_vessels.put({
      id: MONITORING_VESSEL.ID,
      vessel_ids: vesselIds,
      updated_at: new Date(),
    });
  }

  removeAllMonitoringVessel() {
    return true;
  }

  async getDefaultPosition() {
    let res = await localDb.settings.get(SETTINGS.DEFAULT_POSITION_ID);
    if (res === undefined) {
      await localDb.settings.put({
        id: SETTINGS.DEFAULT_POSITION_ID,
        default_position: MAP_DEFAULT_POSITION,
        updated_at: new Date(),
      });
      return MAP_DEFAULT_POSITION;
    }
    return res.default_position;
  }

  async saveDefaultPosition(defaultPosition) {
    await localDb.settings.put({
      id: 1,
      default_position: defaultPosition,
    });
  }

  async getMapStyle() {
    let res = await localDb.settings.get(SETTINGS.MAP_STYLE_ID);
    if (res === undefined) {
      await localDb.settings.put({
        id: SETTINGS.MAP_STYLE_ID,
        map_style: LayerMixin.MAP_DEFAULT_STYLE_URL,
      });
      return LayerMixin.MAP_DEFAULT_STYLE_URL;
    }
    return LayerMixin.methods.convertToNewStyleMap(res.map_style);
  }

  async saveMapStyle(mpaStyle) {
    await localDb.settings.put({
      id: SETTINGS.MAP_STYLE_ID,
      map_style: mpaStyle,
    });
  }

  async getVoyageHistoryTimeLineData(id) {
    return await localDb.voyage_history_timeline_data.get(id);
  }

  async saveVoyageHistoryTimeLineData(data) {
    await localDb.voyage_history_timeline_data.put(data);
  }

  async countVoyageHistoryTimeLineData() {
    return await localDb.voyage_history_timeline_data.count();
  }

  async clearVoyageHistoryTimeLineData() {
    return await localDb.voyage_history_timeline_data.clear();
  }

  async getFilteredVessels() {
    let res = await localDb.filters.get(FILTERS.VESSEL_ID);
    if (res === undefined) {
      let vesselCategoryIds = VesselMixin.methods.getVesselCategoryIds();
      await localDb.filters.put({
        id: FILTERS.VESSEL_ID,
        filter_values: vesselCategoryIds,
      });
      return vesselCategoryIds;
    }
    return res.filter_values;
  }

  async saveFilterVessels(filterValues) {
    return await localDb.filters.put({
      id: FILTERS.VESSEL_ID,
      filter_values: filterValues,
    });
  }

  async getFilterPlots() {
    let res = await localDb.filters.get(FILTERS.PLOT_ID);
    if (res === undefined) {
      let plotCategoryIds = PlotMixin.methods.getPlotParentCategoryIds();
      await localDb.filters.put({
        id: FILTERS.PLOT_ID,
        filter_values: plotCategoryIds,
      });
      return plotCategoryIds;
    }
    return res.filter_values;
  }

  async saveFilterPlots(filterValues) {
    return await localDb.filters.put({
      id: FILTERS.PLOT_ID,
      filter_values: filterValues,
    });
  }

  async getFilterNavigations() {
    let res = await localDb.filters.get(FILTERS.NAVIGATION_ID);
    if (res === undefined) {
      return [];
    }
    return res.filter_values;
  }

  async saveFilterNavigations(filterValues) {
    return await localDb.filters.put({
      id: FILTERS.NAVIGATION_ID,
      filter_values: filterValues,
    });
  }

  async getFilterRadarGrid() {
    const res = await localDb.filters.get(FILTERS.RADAR_GRID_ID);
    return res === undefined ? [] : res.filter_values;
  }

  async saveFilterRadarGrid(filterValues) {
    return await localDb.filters.put({
      id: FILTERS.RADAR_GRID_ID,
      filter_values: filterValues,
    });
  }

  async getOwnVesselDisplayDurationTime() {
    let res = await localDb.settings.get(
      SETTINGS.OWN_VESSEL_DISPLAY_DURATION_TIME_ID
    );
    if (res === undefined) {
      await localDb.settings.put({
        id: SETTINGS.OWN_VESSEL_DISPLAY_DURATION_TIME_ID,
        duration_time: 0,
      });
      return 0;
    }
    return res.duration_time;
  }

  async saveOwnVesselDisplayDurationTime(time) {
    if (time === undefined) return;
    return await localDb.settings.put({
      id: SETTINGS.OWN_VESSEL_DISPLAY_DURATION_TIME_ID,
      duration_time: time,
    });
  }

  async saveLiveMapVessels(data) {
    await localDb.live_map_vessels.put(data);
  }

  async getLiveMapVessels(id) {
    return await localDb.live_map_vessels.get(id);
  }

  /**
   * 有効な船舶一覧を取得
   * @param time
   * @returns {Promise<Array<unknown>>}
   */
  async getCurrentLiveMapVessels(time) {
    return await localDb.live_map_vessels
      .where("time")
      .above(time)
      .toArray();
  }

  /**
   * 古い船舶データを削除
   * @param time
   * @returns {Promise<void>}
   */
  async clearOldLiveMapVessels(time) {
    let list = await localDb.live_map_vessels
      .where("time")
      .below(time)
      .toArray();
    let keys = list.map(row => row.id);
    return await localDb.live_map_vessels.bulkDelete(keys);
  }

  /**
   * 機関データのチャンネル座標情報を取得.
   */
  async getChannelData(channelId) {
    return await localDb.channels_data.get(channelId);
  }

  /**
   * 機関データのチャンネル座標情報を保存.
   */
  async saveChannelData(channelId, payload) {
    // channel一つに対してpoint情報を保存する
    let obj = {
      channel_id: channelId,
      point_data: payload,
    };
    await localDb.channels_data.put(obj);
  }

  /**
   * 機関データのチャンネル座標情報をクリア.
   */
  async clearChannelData() {
    await localDb.channels_data.clear();
  }

  /**
   * 左メニューの状態を取得.
   */
  async getLeftMenuReduceState(
    reduceStateId = SETTINGS.LEFT_MENU_REDUCE_STATE_ID
  ) {
    const reduceState = (await localDb.settings.get(reduceStateId)) || {
      id: SETTINGS.LEFT_MENU_REDUCE_STATE_ID,
      left_menu_reduce_state: DEFAULT_REDUCE_STATE,
    };
    return reduceState;
  }

  /**
   * 保存されている左メニューの状態が縮小状態であるか判定
   */
  async isLeftMenuReduceStateTrue(
    reduceStateId = SETTINGS.LEFT_MENU_REDUCE_STATE_ID
  ) {
    const reduceState = await this.getLeftMenuReduceState(reduceStateId);
    const isReduceStateTrue = reduceState.left_menu_reduce_state === true;
    return isReduceStateTrue;
  }

  /**
   * 左メニューの情報を保存.
   */
  async saveLeftMenuReduceState({
    reduceStateId = SETTINGS.LEFT_MENU_REDUCE_STATE_ID,
    reduceState = DEFAULT_REDUCE_STATE,
  } = {}) {
    // レコードは常に一つ
    let obj = {
      id: reduceStateId,
      left_menu_reduce_state: reduceState,
    };
    await localDb.settings.put(obj);
  }

  async cleanup() {
    try {
      await Promise.allSettled(
        localDb.tables.map(async table => {
          if (table.name === "auth") {
            return await authService.clearNonPersistentData();
          }
          return await localDb.table(table.name).clear();
        })
      ); // ログアウトしてもindexedDBに保存が必要なデータ以外を削除
      Vue.$log.info("== Successful cleanup Aisea LocalDB ======");
    } catch (e) {
      Vue.$log.error("== Failure to cleanup Aisea LocalDB ======", e);
    }
  }
}

export default new LocalDbService();
